import {
  Children,
  cloneElement,
  createContext,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useRef,
} from "react";
import useEventListener from "./useEventListener";

// The default value of this context is a simple function which inverts a Boolean.
// See "ClickOutsideOverride" below for overriding it
export const OverrideContext = createContext(
  (isClickedInside: boolean, _e: MouseEvent) => !isClickedInside
);

// https://webrix.amdocs.com/docs/fhooks/useclickoutside

/**
 * This custom hook receives a callback and returns a click capture handler to be used on the component
 * that you want to detect clicks outside of.
 *
 * NOTE: The callback must be wrapped in useCallback with the proper list of dependencies in order
 * to reattach document events when they change, otherwise this hook will not work properly.
 *
 * This solution is based on a similar solution taken from https://github.com/adazzle/react-data-grid/pull/1417/files#diff-fdcbe490b776c1eed24c88d4b5f27d66R4
 * In short, we leverage the power of event bubbling in React, which works based on React's Component tree
 * rather than the DOM tree, allowing us to accurately detect clicks through portals.
 * So once a click is detected in the component tree of the target component, we set a flag, which the
 * document event handler is checking against. When set to true, the click is considered to be "inside"
 * and so it is ignored.
 *
 * This solution is further improved by separating the "click" event into "mousedown" and "mouseup",
 * allowing us to cover cases where the mouse is clicked inside the element, but released outside the
 * element (for example, when dragging). In such cases the click is still considered as an inside click,
 * since it originated inside the element.
 */

export const useClickOutside = (callback: (e: MouseEvent) => void) => {
  const isClickedInside = useRef(false);
  const isClickedOutside = useContext(OverrideContext);

  useEventListener(
    "mousedown",
    (e: MouseEvent) => {
      isClickedInside.current = !isClickedOutside(isClickedInside.current, e);
    },
    { current: window },
    { capture: true }
  );

  useEventListener(
    "mouseup",
    (e: MouseEvent) => {
      isClickedOutside(isClickedInside.current, e)
        ? callback(e)
        : // biome-ignore lint/suspicious/noAssignInExpressions: OK
          (isClickedInside.current = false);
    },
    { current: window },
    { capture: true }
  );

  return useCallback((): void => {
    isClickedInside.current = true;
  }, [isClickedInside]);
};

// Declarative component:
interface ClickOutsideProps {
  children: ReactElement<any>;
  onClickOutside: (e: MouseEvent) => void;
}

export const ClickOutside = ({ children, onClickOutside }: ClickOutsideProps) => {
  const handleOnMouseDownCapture = useClickOutside(onClickOutside);
  // Updating the contained element instead of adding a wrapper since adding
  // a wrapper may affect the styling/behavior
  return cloneElement(Children.only(children), {
    onMouseDownCapture: handleOnMouseDownCapture,
  });
};

// It is sometimes necessary to modify the condition of the click-outside handler
// of one or more elements. This can be done using the ClickOutsideOverride
interface ClickOutsideOverrideProps {
  condition: (isClickedInside: boolean, event: MouseEvent) => boolean;
  children: ReactNode;
}

export const ClickOutsideOverride = ({
  condition,
  children,
}: ClickOutsideOverrideProps) => (
  <OverrideContext.Provider value={condition}>{children}</OverrideContext.Provider>
);
